// Layer 4 (UI) — the Priority matrix tab, per the locked comp (variant A,
// picked 25 Sep): four neutral counts, the impact × effort grid with each
// opportunity named in its cell and the low-effort column a neutral band
// under an ink rule, and the top five by benefit as rank bars (rank 1
// crimson). Every id opens its row in the register. Presentational only.

import { Commentable } from '@/shared/comments/Commentable';
import { cn } from '@/shared/cn';
import { KpiCard } from '@/versions/v1/modules/_viz';
import { DowntimeCard, RankBar, SYN_CHIP } from '@/versions/v1/modules/downtime/DowntimeCard';
import { KpiStrip } from '@/versions/v1/modules/maintenance/KpiStrip';
import {
  MATRIX_AXIS_LABELS,
  improvementMatrix,
  matrixKpis,
  topByBenefit,
  type EffortBand,
  type MatrixCell,
} from './ImprovementMatrix.transform';

function GridCell({
  cell,
  row,
  column,
  onOpenOpportunity,
}: {
  cell: MatrixCell;
  row: number;
  column: number;
  onOpenOpportunity: (id: string) => void;
}) {
  const empty = cell.items.length === 0;
  return (
    <div
      data-matrix-cell={`${cell.impact}-${cell.effort}`}
      className={cn(
        'relative z-10 flex min-h-[5.25rem] min-w-0 flex-col justify-center gap-1 rounded-md px-2.5 py-2',
        cell.effort === 1
          ? ''
          : empty
            ? 'border border-dashed border-[var(--hairline-strong)]'
            : 'border border-[var(--hairline)] bg-[var(--surface-1)]',
      )}
      style={{ gridRow: row, gridColumn: column }}
    >
      {cell.items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onOpenOpportunity(item.id)}
          className="flex min-w-0 items-baseline gap-2 text-left text-sm text-ink-primary hover:underline"
        >
          <span className="shrink-0 font-mono text-[10px] text-muted">{item.id}</span>
          <span className="min-w-0">{item.opportunity}</span>
        </button>
      ))}
    </div>
  );
}

/** The low-effort column as one neutral band under a 2 px ink rule, painted
 *  behind its three cells so the column reads as a region. */
function LowEffortBand({ rowCount }: { rowCount: number }) {
  return (
    <div
      aria-hidden
      className="pointer-events-none rounded-sm border-t-2 border-ink-primary bg-[color-mix(in_srgb,var(--ink-secondary)_12%,var(--surface-2))]"
      style={{ gridColumn: 2, gridRow: `1 / span ${rowCount}`, zIndex: 0 }}
    />
  );
}

export function ImprovementMatrix({ onOpenOpportunity }: { onOpenOpportunity: (id: string) => void }) {
  const matrix = improvementMatrix();
  const top = topByBenefit();

  return (
    <div className="space-y-3">
      <KpiStrip>
        {matrixKpis().map((card) => (
          <KpiCard key={card.label} label={card.label} value={card.value} sub={card.sub} tone="neutral" />
        ))}
      </KpiStrip>

      <div className="grid min-w-0 gap-3 xl:grid-cols-12 xl:items-stretch">
        <Commentable name="ImprovementMatrix">
          <div className="min-w-0 xl:col-span-8">
            <DowntimeCard
              title="Impact against effort"
              meta="impact = benefit / yr · effort = capex"
              actions={SYN_CHIP}
              className="h-full"
            >
              <div className="grid grid-cols-[auto_repeat(3,minmax(0,1fr))] gap-1.5">
                <LowEffortBand rowCount={matrix.rows.length} />
                {matrix.rows.flatMap((row, rowIndex) => [
                  <span
                    key={`impact-${row.impact}`}
                    className="self-center pr-2 text-right text-xs text-muted"
                    style={{ gridRow: rowIndex + 1, gridColumn: 1 }}
                  >
                    {MATRIX_AXIS_LABELS[row.impact]}
                  </span>,
                  ...row.cells.map((cell, cellIndex) => (
                    <GridCell
                      key={`${cell.impact}-${cell.effort}`}
                      cell={cell}
                      row={rowIndex + 1}
                      column={cellIndex + 2}
                      onOpenOpportunity={onOpenOpportunity}
                    />
                  )),
                ])}
                <span style={{ gridRow: matrix.rows.length + 1, gridColumn: 1 }} />
                {matrix.effortTicks.map((effort: EffortBand) => (
                  <span
                    key={`effort-${effort}`}
                    className={cn('pt-1 text-center text-xs', effort === 1 ? 'font-semibold text-ink-primary' : 'text-muted')}
                    style={{ gridRow: matrix.rows.length + 1, gridColumn: effort + 1 }}
                  >
                    {MATRIX_AXIS_LABELS[effort]} effort
                  </span>
                ))}
              </div>
            </DowntimeCard>
          </div>
        </Commentable>

        <DowntimeCard title="Top 5 by benefit / yr" className="xl:col-span-4">
          <ol>
            {top.map((row, index) => (
              <li key={row.id} className="border-b border-[var(--hairline)] py-2 last:border-b-0">
                <button
                  type="button"
                  onClick={() => onOpenOpportunity(row.id)}
                  className="flex w-full items-baseline justify-between gap-3 text-left"
                >
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span
                      className={cn(
                        'shrink-0 font-mono text-[10px]',
                        index === 0 ? 'font-semibold text-[var(--accent)]' : 'text-muted',
                      )}
                    >
                      {row.id}
                    </span>
                    <span className="truncate text-sm text-ink-primary">{row.opportunity}</span>
                  </span>
                  <span className="whitespace-nowrap text-sm font-semibold tabular-nums text-ink-primary">{row.benefit}</span>
                </button>
                <RankBar ratio={row.ratio} tone={index === 0 ? 'top' : 'ink'} className="mt-1.5" />
              </li>
            ))}
          </ol>
        </DowntimeCard>
      </div>
    </div>
  );
}
